import { useQuery } from '@tanstack/react-query';
import { Radio } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { CenterFlex, Flex, Space, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import LazyLoader from '@/components/Common/LazyLoader';
import Spinner from '@/components/Common/Spinner';
import Transition from '@/components/Common/Transition';
import Input from '@/components/Inputs/Input';
import { useModalContext } from '@/components/Modal/Modal';
import TreeSelect from '@/components/Select/TreeSelect';
import Typo from '@/components/Typography/Typo';
import AMP_KEYS from '@/configs/amp-track-keys';
import { ApiError } from '@/error-handler/ApiError';
import {
  DASHBOARD_REDIRECT_ID_PARAM,
  DASHBOARD_REDIRECT_ROW_INDEX_PARAM,
  Dashboard2ColumnType,
} from '@/features/Dashboard/types/constants';
import dashboardTreeFetcher from '@/features/Dashboard/utils/dashboardTreeFetcher';
import useColor from '@/hooks/domain/useColor';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import { MAX_LAST_ROW_POS } from '@/services/dashboard2';
import service from '@/services/index';
import { useToast } from '@/stores/pure/ToastStore';
import { modalStyle } from '@/styles/style';
import { track } from '@/utils/track/track';

type Props = {
  chartId?: string;
  mode: 'save' | 'add';
  createChart: (
    callback?: (chart: ChartItem) => Promise<void>,
  ) => Promise<void>;
  updateChart: (
    callback?: (chart: ChartItem) => Promise<void>,
  ) => Promise<void>;
};

type RadioGroupItem = {
  id: string;
  label: string;
  sibling?: React.ReactNode;
  ctaBtnLabel: string;
  onConfirm: () => Promise<void>;
};

const AddChartToDashboardModal = (props: Props) => {
  const { closeModal } = useModalContext();
  const wr = useWorkspaceRouter();
  const addToast = useToast();
  const color = useColor();
  const [searchParams] = useSearchParams();
  const redirectDashboardID = searchParams.get(DASHBOARD_REDIRECT_ID_PARAM);
  const redirectDashboardRowIndex = searchParams.get(
    DASHBOARD_REDIRECT_ROW_INDEX_PARAM,
  );
  const [dashboardName, setDashboardName] = useState<string>('');

  const { data: dashboard, isLoading: isDashboardLoading } = useQuery({
    queryKey: ['dashboard2', redirectDashboardID],
    enabled: !!redirectDashboardID,
    queryFn: async () =>
      await service.dashboard2.fetchDashboard(redirectDashboardID!),
  });

  const defaultOption = useMemo(() => {
    if (!dashboard) return null;
    return dashboard.alias;
  }, [dashboard]);

  const [selectedOption, setSelectedOption] = useState<string | null>(
    defaultOption,
  );

  const [dashboardValidationFailed, setDashboardValidationFailed] =
    useState(false);

  const onSelectDashboard = async (dashboardAlias: string) => {
    setSelectedOption(dashboardAlias);

    if (!props.chartId) return;

    try {
      await service.dashboard2.validateChartAdditionToDashboard(
        dashboardAlias,
        props.chartId,
      );
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.code === 409) {
          setDashboardValidationFailed(true);
          return;
        }
      }
      return;
    }

    setDashboardValidationFailed(false);
  };

  useEffect(() => {
    setSelectedOption(defaultOption);
  }, [defaultOption]);

  const items = {
    SAVE_CHART: {
      id: 'SAVE_CHART',
      label: "Don't add to a dashboard",
      ctaBtnLabel: 'Save',
      onConfirm: async () => {
        if (!props.chartId) {
          await props.createChart(closeModal);
          return;
        }

        // save chart
        await props.updateChart(closeModal);
      },
    },
    ADD_TO_DASHBOARD: {
      id: 'ADD_TO_DASHBOARD',
      label: 'Add to existing dashboard',
      ctaBtnLabel: 'Save and add',
      onConfirm: async () => {
        const dashboardAlias = selectedOption;

        if (!dashboardAlias) {
          return;
        }

        if (dashboardValidationFailed) {
          return;
        }
        const pos = redirectDashboardRowIndex
          ? Number(redirectDashboardRowIndex)
          : MAX_LAST_ROW_POS;

        // chart create mode
        if (!props.chartId) {
          await props.createChart(async (chart: ChartItem) => {
            if (!chart.alias) {
              return;
            }

            try {
              // add to existing dashboard
              await service.dashboard2.addDashboardRow(dashboardAlias, {
                pos,
                columns: [
                  {
                    type: Dashboard2ColumnType.DASHBOARD_COLUMN_CHART,
                    chart: chart.alias,
                  },
                ],
              });

              // Track chart save to dashboard complete event
              track(AMP_KEYS.CHART_SAVE_TO_DASHBOARD_COMPLETE, {
                chart_alias: chart.alias,
                dashboard_alias: dashboardAlias,
              });

              if (redirectDashboardID) {
                const url = `/dashboard2/${selectedOption}`;
                addToast(
                  <Typo
                    type="body2"
                    weight="sb"
                    color={color.gray100}
                    style={{ cursor: 'pointer' }}
                  >
                    Chart added to Dashboard.{' '}
                    <Typo
                      style={{ textDecoration: 'underline' }}
                      onClick={() => {
                        wr.push(url);
                      }}
                    >
                      Go to your dashboard
                    </Typo>
                  </Typo>,
                  'success',
                  3000,
                );
              } else {
                addToast('Chart added to Dashboard', 'success');
              }

              closeModal();
            } catch (e) {
              addToast('Failed to add chart to dashboard', 'fail');
            }
          });
        }
        // chart edit mode
        else {
          await props.updateChart(async () => {
            try {
              // add to existing dashboard
              await service.dashboard2.addDashboardRow(dashboardAlias, {
                pos,
                columns: [
                  {
                    type: Dashboard2ColumnType.DASHBOARD_COLUMN_CHART,
                    chart: props.chartId!,
                  },
                ],
              });

              // Track chart save to dashboard complete event
              track(AMP_KEYS.CHART_SAVE_TO_DASHBOARD_COMPLETE, {
                chart_alias: props.chartId!,
                dashboard_alias: dashboardAlias,
              });

              if (redirectDashboardID) {
                const url = `/dashboard2/${selectedOption}`;
                addToast(
                  <Typo
                    type="body2"
                    weight="sb"
                    color={color.gray100}
                    style={{ cursor: 'pointer' }}
                  >
                    Chart added to Dashboard.{' '}
                    <Typo
                      style={{ textDecoration: 'underline' }}
                      onClick={() => {
                        wr.push(url);
                      }}
                    >
                      Go to your dashboard
                    </Typo>
                  </Typo>,
                  'success',
                  3000,
                );
              } else {
                addToast('Chart added to Dashboard', 'success');
              }

              closeModal();
            } catch (e) {
              addToast('Failed to add chart to dashboard', 'fail');
            }
          });
        }
      },
      sibling: (
        <Transition
          status={!isDashboardLoading}
          fallback={
            <LazyLoader
              show={isDashboardLoading}
              delay={300}
              loader={
                <CenterFlex height="40px">
                  <Spinner />
                </CenterFlex>
              }
            />
          }
        >
          <TreeSelect
            mode="single"
            placeholder="Select dashboard"
            values={selectedOption ? [selectedOption] : []}
            fetcher={dashboardTreeFetcher}
            onChange={(values) => {
              if (!values[0]) {
                setSelectedOption(null);
                return;
              }
              onSelectDashboard(values[0] as string);
            }}
          />
          {dashboardValidationFailed && (
            <React.Fragment>
              <Space h="8px" />
              <Typo type="label3" className="text-destructive">
                This chart is already in the dashboard
              </Typo>
            </React.Fragment>
          )}
        </Transition>
      ),
    },
    CREATE_NEW_DASHBOARD: {
      id: 'CREATE_NEW_DASHBOARD',
      label: 'Add to new dashboard',
      ctaBtnLabel: 'Save and add',
      sibling: (
        <div>
          <Typo type="label2" weight="m" className="text-gray-700">
            Dashboard name
          </Typo>
          <Space h="8px" />
          <Input
            initFocus
            placeholder="example"
            sizeType="l"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setDashboardName(e.currentTarget.value);
            }}
            value={dashboardName}
          />
        </div>
      ),
      onConfirm: async () => {
        try {
          // create new dashboard
          const { alias: dashboardAlias } =
            await service.dashboard2.createDashboard(dashboardName);

          const pos = MAX_LAST_ROW_POS;

          if (!props.chartId) {
            props.createChart(async (chart: ChartItem) => {
              if (!chart.alias) {
                return;
              }

              // add new row to the dashboard
              await service.dashboard2.addDashboardRow(dashboardAlias, {
                pos,
                columns: [
                  {
                    type: Dashboard2ColumnType.DASHBOARD_COLUMN_CHART,
                    chart: chart.alias,
                  },
                ],
              });
            });
          } else {
            await props.updateChart(async () => {
              // add new row to the dashboard
              await service.dashboard2.addDashboardRow(dashboardAlias, {
                pos,
                columns: [
                  {
                    type: Dashboard2ColumnType.DASHBOARD_COLUMN_CHART,
                    chart: props.chartId!,
                  },
                ],
              });
            });
          }

          if (redirectDashboardID) {
            wr.push('/dashboard2/' + selectedOption);
            return;
          }

          addToast('Chart added to new dashboard', 'success');
          closeModal();
        } catch (e) {
          addToast('Failed to add chart to dashboard', 'fail');
        }
      },
    },
  };

  const radioGroupItems: { items: RadioGroupItem[]; default: string } =
    props.mode === 'add'
      ? {
          items: [items['ADD_TO_DASHBOARD'], items['CREATE_NEW_DASHBOARD']],
          default: items['ADD_TO_DASHBOARD'].id,
        }
      : redirectDashboardID
        ? {
            items: [
              items['SAVE_CHART'],
              items['ADD_TO_DASHBOARD'],
              items['CREATE_NEW_DASHBOARD'],
            ],
            default: items['ADD_TO_DASHBOARD'].id,
          }
        : {
            items: [
              items['SAVE_CHART'],
              items['ADD_TO_DASHBOARD'],
              items['CREATE_NEW_DASHBOARD'],
            ],
            default: items['SAVE_CHART'].id,
          };

  const [selectedRadioGroupItem, setSelectedRadioGroupItem] = useState<string>(
    radioGroupItems.default,
  );

  return (
    <StyledCreateDashboardModal>
      <Typo type="label0" weight="sb" color={color.gray800}>
        {props.mode === 'save' ? 'Save chart' : 'Add to dashboard'}
      </Typo>
      <Space h="32px" />

      <Radio.Group
        value={selectedRadioGroupItem}
        onChange={(e) => {
          setSelectedRadioGroupItem(e.target.value);
        }}
        style={{
          width: '100%',
        }}
      >
        <VFlex g={8}>
          {radioGroupItems.items.map((item) => (
            <div key={item.id}>
              <Radio value={item.id}>{item.label}</Radio>
              <Space h="8px" />
              {selectedRadioGroupItem === item.id && item.sibling}
            </div>
          ))}
        </VFlex>
      </Radio.Group>

      <Space h="40px" />
      <Flex j="space-between">
        <Flex style={{ paddingLeft: 20 }}></Flex>
        <Flex a="center" g={12}>
          <Button onClick={closeModal} sizeType="m" variant="secondary">
            Cancel
          </Button>

          <Button
            sizeType="m"
            variant="primary"
            onClick={() => {
              items[selectedRadioGroupItem as keyof typeof items].onConfirm();
            }}
            disabled={
              (selectedRadioGroupItem === 'ADD_TO_DASHBOARD' &&
                (!selectedOption || dashboardValidationFailed)) ||
              (selectedRadioGroupItem === 'CREATE_NEW_DASHBOARD' &&
                !dashboardName)
            }
          >
            {items[selectedRadioGroupItem as keyof typeof items].ctaBtnLabel}
          </Button>
        </Flex>
      </Flex>
    </StyledCreateDashboardModal>
  );
};

export default AddChartToDashboardModal;

const StyledCreateDashboardModal = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  ${modalStyle};
  width: 600px;
  background-color: ${(props) => props.theme.colors.gray100};
  padding: 24px;
  .icon {
    margin-top: 4px;
  }
`;
